import { ComponentType, ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { Trans } from 'services/i18n';

import { EmptyState, NoResult } from 'ui/illustrations';
import { spacing, colors } from 'ui/theme';
import { body1, heading3, link } from 'ui/theme/typography';
import { pxToRem } from 'utils/convert';

import { useTranslations } from '../hooks/useTranslations';

const EmptyStateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${pxToRem(400)};
`;

const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${pxToRem(461)};
`;

const MainStyling = styled.div`
  margin-bottom: ${spacing.half};
  ${heading3};
`;

const CaptionStyling = styled.div`
  text-align: center;
  ${body1};
  color: ${colors.textSecondary};
`;

const StyledHref = styled.a`
  ${link};
`;

type ImageProps = {
  height?: number;
  width?: number;
};

interface EmptyStateGeneratorProps {
  Image: ComponentType<React.PropsWithChildren<ImageProps>>;
  children: ReactNode;
  mainText: string;
  testId: string;
}

export const testIds = {
  generalEmptyState: 'df-index-empty-state-general',
  noMatchingResults: 'df-index-empty-state-no-results',
};

const EmptyStateGenerator = ({ Image, mainText, testId, children }: EmptyStateGeneratorProps) => {
  const { t } = useTranslations();

  useEffect(() => {
    document.title = `${mainText} | ${t('header.dataFeeds')}`;
    return () => {
      document.title = t('header.dataFeeds');
    };
  }, [mainText]);

  return (
    <EmptyStateWrapper data-test={testId}>
      <EmptyStateContent>
        <Image />
        <MainStyling>{mainText}</MainStyling>
        <CaptionStyling>{children}</CaptionStyling>
      </EmptyStateContent>
    </EmptyStateWrapper>
  );
};

export const GeneralEmptyState = (): JSX.Element => {
  const { t } = useTranslations();

  return (
    <EmptyStateGenerator
      Image={EmptyState}
      mainText={t('caption.noDataFeedsYet')}
      testId={testIds.generalEmptyState}
    >
      <div>
        <Trans
          t={t}
          i18nKey="caption.learnMoreCTA"
          components={{
            href: <StyledHref href={'https://support.iterable.com/'} />,
          }}
        >
          Learn more by visiting our{' '}
          <StyledHref href={'https://support.iterable.com/'}>Knowledge Center</StyledHref>.
        </Trans>
      </div>
    </EmptyStateGenerator>
  );
};

export const NoResults = (): JSX.Element => {
  const { t } = useTranslations();

  return (
    <EmptyStateGenerator
      Image={NoResult}
      mainText={t('caption.noMatchingResults')}
      testId={testIds.noMatchingResults}
    >
      {t('caption.noMatchingResultCTA')}
    </EmptyStateGenerator>
  );
};
